import { LoaderCircle } from 'lucide-solid';
import { createSignal, Show, type JSX } from 'solid-js';
import { PAIRING_FAILURE, reportError } from '../lib/plainErrors';
import { useVenue } from '../state/VenueProvider';
import { TEST_IDS } from '../testIds';

/**
 * The controller's own console, opened over a cable.
 *
 * A technician plugs a laptop into the Pi's Ethernet port and opens `http://<controller>/`. This
 * bundle is served BY the controller, so the only connection it can make is the direct one and the
 * only thing it needs is the venue's setup code. There is no sign-in and no transport choice here,
 * and there is none when the venue's internet happens to be up either: this is the cable console.
 */
export function LocalConsoleSetup(): JSX.Element {
  const venue = useVenue();
  const [setupCode, setSetupCode] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [problem, setProblem] = createSignal('');

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setProblem('');
    try {
      await venue.pairLan(setupCode());
    } catch (error) {
      setProblem(reportError('lan pairing', error, PAIRING_FAILURE).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main class="signin-shell">
      <section class="signin-card" aria-labelledby="local-console-title">
        <div class="brand-lockup brand-lockup-large">
          <img src="/brand/nuance-logo-256.png" alt="" />
          <span>Nuance Venue</span>
        </div>
        <form class="signin-form" onSubmit={(event) => void submit(event)}>
          <header><h1 id="local-console-title">{venue.lanHello()?.deviceName || 'Nuance Venue controller'}</h1></header>
          <label>
            <span>Enter the venue's setup code</span>
            <input
              type="password"
              autocomplete="one-time-code"
              required
              value={setupCode()}
              onInput={(event) => setSetupCode(event.currentTarget.value)}
              data-testid={TEST_IDS.pairSetupCode}
            />
          </label>
          <Show when={problem() || venue.lanReason()}>
            <p class="form-error" role="alert">{problem() || venue.lanReason()}</p>
          </Show>
          <button
            class="button button-primary button-large"
            type="submit"
            disabled={busy() || !setupCode().trim()}
            data-testid={TEST_IDS.pairLan}
          >
            <Show when={busy()}><LoaderCircle class="spin" size={18} /></Show>
            Connect
          </button>
        </form>
      </section>
    </main>
  );
}
